import { useRef, useState } from 'react';
import type {
  SwipeableHandlers,
  SwipeDirections,
  SwipeEventData
} from 'react-swipeable';
import { DOWN, LEFT, RIGHT, UP, useSwipeable } from 'react-swipeable';

type UseSwipeArgs<T extends () => void> = {
  onSwipe: T;
  excludeDirections?: SwipeDirections[];
  velocityToApplySwipe?: number;
  thresholdX?: number;
  thresholdY?: number;
};

export function useSwipe<T extends () => void>({
  onSwipe,
  excludeDirections = [],
  velocityToApplySwipe = 0.4,
  thresholdX = 100,
  thresholdY = 50
}: UseSwipeArgs<T>): [
  (el: HTMLDivElement) => void,
  SwipeableHandlers,
  SwipeDirections | null
] {
  const [direction, setDirection] = useState<SwipeDirections | null>(null);
  const swipeElement = useRef<HTMLDivElement | null>(null);
  const swipeStartHandler = ({ dir }: SwipeEventData) => {
    if (swipeElement.current && !excludeDirections.includes(dir)) {
      swipeElement.current.style.transition = '';
      swipeElement.current.style.pointerEvents = 'none';
    }
  };

  const swipeHandler = ({ deltaX, deltaY, dir }: SwipeEventData) => {
    if (swipeElement.current && !excludeDirections.includes(dir)) {
      if (direction !== dir) {
        setDirection(dir);
      }
      const DELTA_X = Math.abs(deltaX);
      const DELTA_Y = Math.abs(deltaY);
      if (dir === LEFT && DELTA_X <= thresholdX) {
        swipeElement.current.style.transform = `translateX(${-DELTA_X}px)`;
      }
      if (dir === RIGHT && DELTA_X <= thresholdX) {
        swipeElement.current.style.transform = `translateX(${DELTA_X}px)`;
      }
      if (dir === UP && DELTA_Y <= thresholdY) {
        swipeElement.current.style.transform = `translateY(${-DELTA_Y}px)`;
      }
      if (dir === DOWN && DELTA_Y <= thresholdY) {
        swipeElement.current.style.transform = `translateY(${DELTA_Y}px)`;
      }
    }
  };

  const swipeEndHandler = ({
    velocity,
    deltaX,
    deltaY,
    dir
  }: SwipeEventData) => {
    if (swipeElement.current && !excludeDirections.includes(dir)) {
      if (
        velocity > velocityToApplySwipe ||
        Math.abs(deltaX) >= thresholdX ||
        Math.abs(deltaY) >= thresholdY
      ) {
        if (onSwipe) {
          onSwipe();
        }
      }

      swipeElement.current.style.transition = 'transform 0.3s ease';
      swipeElement.current.style.transform = '';

      setTimeout(() => {
        if (swipeElement.current) {
          swipeElement.current.style.pointerEvents = 'auto';
        }
        setDirection(null);
      }, 250);
    }
  };

  const handlers = useSwipeable({
    onSwipeStart: swipeStartHandler,
    onSwiping: swipeHandler,
    onSwiped: swipeEndHandler,
    trackMouse: true,
    trackTouch: true,
    preventScrollOnSwipe: true
  });

  const refPassthrough = (el: HTMLDivElement) => {
    swipeElement.current = el;
    handlers.ref(el);
  };

  return [refPassthrough, handlers, direction];
}
